import {
  Component,
  PLATFORM_ID,
  Type,
  ViewEncapsulation,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { NgComponentOutlet, isPlatformBrowser } from '@angular/common';

export interface ResolvedBlock {
  component: Type<unknown>;
  inputs: Record<string, unknown>;
}

export interface TabItem {
  label: string;
  content: string;
  image?: string;
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
  resolvedBlocks?: ResolvedBlock[];
}

@Component({
  selector: 'ds-tab-block',
  templateUrl: './tab-block.html',
  styleUrl: './tab-block.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [NgComponentOutlet],
})
export class TabBlockComponent {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly tabs = input.required<TabItem[]>();

  activeIndex = signal(0);
  entering = signal(false);
  activeTab = computed(() => this.tabs()[this.activeIndex()]);

  selectTab(index: number): void {
    if (index === this.activeIndex()) {
      return;
    }
    // Respect OS-level reduced-motion preference — SSR-safe (isBrowser guard
    // keeps window.matchMedia out of the Node render path). On match, apply
    // the final tab state synchronously and skip the 150ms enter-animation.
    const prefersReducedMotion =
      this.isBrowser &&
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      this.activeIndex.set(index);
      this.entering.set(false);
      return;
    }
    this.entering.set(true);
    setTimeout(() => {
      this.activeIndex.set(index);
      this.entering.set(false);
    }, 150);
  }

  onKeydown(event: KeyboardEvent, index: number): void {
    const count = this.tabs().length;
    let newIndex: number;

    if (event.key === 'ArrowRight') {
      newIndex = (index + 1) % count;
    } else if (event.key === 'ArrowLeft') {
      newIndex = (index - 1 + count) % count;
    } else if (event.key === 'Home') {
      newIndex = 0;
    } else if (event.key === 'End') {
      newIndex = count - 1;
    } else {
      return;
    }

    event.preventDefault();
    this.selectTab(newIndex);

    // Focus the new tab button
    const tablist = (event.target as HTMLElement).closest('[role="tablist"]');
    if (tablist) {
      const buttons = tablist.querySelectorAll<HTMLButtonElement>('[role="tab"]');
      buttons[newIndex]?.focus();
    }
  }
}
